/**
 * Leitura do Excel de clientes no browser (formato do plano §3). As colunas
 * reconhecem-se pelo cabeçalho, sem depender da ordem nem de maiúsculas/acentos.
 */

import readXlsxFile from "read-excel-file"
import type { ClientImportRow } from "../../shared/domain"

type Field = "number" | "estab" | "name" | "email" | "tax_id" | "phone" | "locality" | "surveys_opt_in"

const HEADERS: Record<Field, string[]> = {
  number: ["n.º cliente phc", "n.º cliente", "nº cliente", "no cliente", "numero cliente", "numero", "n.º", "cliente"],
  estab: ["estabelecimento", "estab", "estab."],
  name: ["nome / empresa", "nome/empresa", "nome", "empresa"],
  email: ["email", "e-mail"],
  tax_id: ["nif", "contribuinte"],
  phone: ["telefone", "telemovel", "tlm"],
  locality: ["localidade", "cidade"],
  surveys_opt_in: ["recebe inqueritos (s/n)", "recebe inqueritos", "inqueritos"],
}

export const REQUIRED_FIELDS: Field[] = ["number", "estab", "name", "email"]

const norm = (s: unknown) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()

/** Índice de coluna por campo, a partir da linha de cabeçalho. */
export function mapHeaders(header: unknown[]): { columns: Partial<Record<Field, number>>; missing: Field[] } {
  const cells = header.map(norm)
  const columns: Partial<Record<Field, number>> = {}
  // Primeiro correspondências exatas, depois por prefixo (evita "cliente" apanhar "n.º cliente phc").
  for (const exact of [true, false]) {
    for (const field of Object.keys(HEADERS) as Field[]) {
      if (columns[field] !== undefined) continue
      const idx = cells.findIndex(
        (c, i) =>
          !Object.values(columns).includes(i) &&
          HEADERS[field].some((h) => (exact ? c === norm(h) : c.startsWith(norm(h)))),
      )
      if (idx >= 0) columns[field] = idx
    }
  }
  // O estabelecimento pode faltar no Excel (assume-se 0).
  const missing = REQUIRED_FIELDS.filter((f) => f !== "estab" && columns[f] === undefined)
  return { columns, missing }
}

const cellText = (v: unknown) => {
  if (v == null) return ""
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(v)
  return String(v).trim()
}

export function rowsFromSheet(rows: unknown[][]): { rows: ClientImportRow[]; missing: string[] } {
  if (rows.length === 0) return { rows: [], missing: ["cabeçalho"] }
  const { columns, missing } = mapHeaders(rows[0]!)
  if (missing.length) return { rows: [], missing }
  const get = (r: unknown[], f: Field) => (columns[f] === undefined ? "" : cellText(r[columns[f]!]))
  const out: ClientImportRow[] = []
  rows.slice(1).forEach((r, i) => {
    if (r.every((c) => cellText(c) === "")) return
    const optIn = norm(get(r, "surveys_opt_in"))
    out.push({
      row: i + 2,
      number: get(r, "number"),
      estab: get(r, "estab") || "0",
      name: get(r, "name"),
      email: get(r, "email"),
      tax_id: get(r, "tax_id") || undefined,
      phone: get(r, "phone") || undefined,
      locality: get(r, "locality") || undefined,
      surveys_opt_in: optIn ? !["n", "nao", "no", "0", "false"].includes(optIn) : undefined,
    })
  })
  return { rows: out, missing: [] }
}

export async function readClientsFile(file: File): Promise<{ rows: ClientImportRow[]; missing: string[] }> {
  const sheet = (await readXlsxFile(file)) as unknown[][]
  return rowsFromSheet(sheet)
}

export const FIELD_LABELS: Record<string, string> = {
  number: "N.º cliente PHC",
  estab: "Estabelecimento",
  name: "Nome / Empresa",
  email: "Email",
  cabeçalho: "linha de cabeçalho",
}
